import React, { useState } from 'react';
import { usePayments, useCreatePayment, usePurchaseBills, useSalesInvoices } from '../hooks/useTransactions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { useAccountsCustomers, useAccountsVendors } from '../hooks/useParties';

export function PaymentList({ initialTab = 'receipt' }: { initialTab?: 'receipt' | 'payment' }) {
  const { data: payments, isLoading } = usePayments();
  const { data: customers, isLoading: customersLoading } = useAccountsCustomers();
  const { data: vendors, isLoading: vendorsLoading } = useAccountsVendors();
  const createPayment = useCreatePayment();
  const { data: invoices = [] } = useSalesInvoices();
  const { data: bills = [] } = usePurchaseBills();
  const queryClient = useQueryClient();
  
  const [tab, setTab] = useState<'receipt'|'payment'>(initialTab);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({ date: new Date().toISOString().slice(0,10), partyId: '', amount: 0, paymentMode: 'bank_transfer', reference: '', allocations: {} as Record<string, number> });
  const reversePayment = useMutation({ mutationFn: async ({ id, reason }: { id: string; reason: string }) => api.post(`/api/accounts/payments/${id}/reverse`, { reason }, { headers: { 'X-Idempotency-Key': crypto.randomUUID() } }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['accounts'] }); toast.success('Payment reversed.'); }, onError: (error) => toast.error(getApiErrorMessage(error, 'Could not reverse payment.')) });

  if (isLoading) return <Skeleton className="h-[400px]" />;

  const formatCurrency = (value: unknown) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 })
      .format(Number(value) || 0);

  const filtered = payments?.filter((p: any) => p.type === tab) || [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      type: tab,
      date: formData.date,
      partyType: tab === 'receipt' ? 'customer' : 'vendor',
      partyId: formData.partyId,
      amount: Number(formData.amount),
      paymentMethod: formData.paymentMode,
      referenceNumber: formData.reference
      ,allocations: Object.entries(formData.allocations).filter(([, amount]) => Number(amount) > 0).map(([documentId, amount]) => ({ documentType: tab === 'receipt' ? 'invoice' : 'bill', documentId, amount: Number(amount) }))
    };
    await createPayment.mutateAsync(payload);
    setIsCreating(false);
    setFormData({ date: new Date().toISOString().slice(0,10), partyId: '', amount: 0, paymentMode: 'bank_transfer', reference: '', allocations: {} });
  };

  const selectTab = (nextTab: 'receipt' | 'payment') => {
    setTab(nextTab);
    setFormData(current => ({ ...current, partyId: '', allocations: {} }));
  };

  const partyOptions = tab === 'receipt'
    ? (customers || []).map(customer => ({ value: customer._id, label: customer.name }))
    : (vendors || []).map(vendor => ({
        value: vendor._id,
        label: vendor.name || vendor.contactId?.displayName || vendor.contactId?.name || 'Unnamed vendor',
      }));
  const openDocuments = (tab === 'receipt' ? invoices : bills).filter((document: any) => {
    const documentParty = tab === 'receipt' ? document.customerId : document.vendorId;
    const documentPartyId = typeof documentParty === 'string' ? documentParty : documentParty?._id;
    return documentPartyId === formData.partyId && ['unpaid', 'partially_paid', 'overdue'].includes(document.status) && Number(document.amountDue) > 0;
  });
  const allocatedTotal = Object.values(formData.allocations).reduce((sum, amount) => sum + (Number(amount) || 0), 0);

  return (
    <>
      <Card className="shadow-xs h-full flex flex-col">
        <CardHeader 
          className="flex-none" 
          title="Payments & Receipts" 
          action={
            <div className="flex space-x-4 items-center">
              <div className="flex space-x-2">
                <button 
                  onClick={() => selectTab('receipt')}
                  className={`px-4 py-1 text-sm rounded-md ${tab === 'receipt' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                >
                  Customer Receipts
                </button>
                <button 
                  onClick={() => selectTab('payment')}
                  className={`px-4 py-1 text-sm rounded-md ${tab === 'payment' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                >
                  Vendor Payments
                </button>
              </div>
              <Button size="sm" onClick={() => setIsCreating(true)}>
                + New {tab === 'receipt' ? 'Receipt' : 'Payment'}
              </Button>
            </div>
          } 
        />
        <CardBody className="flex-1 overflow-auto p-0">
          <div className="border rounded-md">
            <div className="grid grid-cols-8 bg-muted/50 py-2 px-4 border-b text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <div>Date</div>
              <div>Voucher #</div>
              <div className="col-span-2">Party</div>
              <div>Mode</div>
              <div>Amount</div><div>Status</div><div className="text-right">Actions</div>
            </div>
            <div className="divide-y">
              {filtered.map((payment: any) => (
                <div key={payment._id} className="grid grid-cols-8 py-3 px-4 text-sm hover:bg-muted/30">
                  <div className="text-muted-foreground">{new Date(payment.date).toLocaleDateString('en-IN')}</div>
                  <div className="font-medium text-foreground">{payment.paymentNumber || payment.voucherNumber || '—'}</div>
                  <div className="col-span-2 truncate">
                    {payment.partyType === 'customer'
                      ? (payment.customerId?.name || payment.partyId?.name || 'Customer')
                      : (payment.vendorId?.name || payment.partyId?.name || 'Vendor')}
                  </div>
                  <div className="capitalize text-muted-foreground">{(payment.paymentMethod || payment.paymentMode || '—').replaceAll('_', ' ')}</div>
                  <div className="font-mono text-green-700">{formatCurrency(payment.amount)}</div>
                  <div className="capitalize">{payment.status}</div><div className="text-right">{payment.status === 'posted' && <Button size="sm" variant="ghost" onClick={() => { const reason = window.prompt('Reason for reversal'); if (reason) reversePayment.mutate({ id: payment._id, reason }); }}>Reverse</Button>}</div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  No {tab}s found.
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      <Modal open={isCreating} onClose={() => setIsCreating(false)} title={`Create ${tab === 'receipt' ? 'Customer Receipt' : 'Vendor Payment'}`}>
        <form onSubmit={handleCreate} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Select
              label={tab === 'receipt' ? 'Customer' : 'Vendor'}
              required
              value={formData.partyId}
              onChange={e => setFormData({ ...formData, partyId: e.target.value, allocations: {} })}
              options={partyOptions}
              placeholder={(tab === 'receipt' ? customersLoading : vendorsLoading) ? 'Loading...' : `Select ${tab === 'receipt' ? 'customer' : 'vendor'}`}
            />
            {!partyOptions.length && !(tab === 'receipt' ? customersLoading : vendorsLoading) && (
              <p className="text-xs text-amber-700">Add a {tab === 'receipt' ? 'customer' : 'vendor'} from Customers & Vendors first.</p>
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Date</label>
            <Input type="date" required value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Amount (₹)</label>
            <Input type="number" required value={formData.amount} onChange={e => setFormData({ ...formData, amount: Number(e.target.value) })} />
          </div>
          {formData.partyId && <div className="space-y-2"><div className="flex items-center justify-between"><label className="text-sm font-medium">Allocate to open {tab === 'receipt' ? 'invoices' : 'bills'} (optional)</label><span className={allocatedTotal > formData.amount ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>{formatCurrency(allocatedTotal)} allocated · {formatCurrency(Math.max(0, formData.amount - allocatedTotal))} advance</span></div><div className="max-h-44 overflow-auto rounded-md border divide-y">{openDocuments.map((document: any) => { const id = document._id; return <div key={id} className="grid grid-cols-[1fr_120px_120px] items-center gap-2 p-2 text-sm"><div><p className="font-medium">{document.invoiceNumber || document.billNumber}</p><p className="text-xs text-muted-foreground">Due {new Date(document.dueDate).toLocaleDateString('en-IN')}</p></div><p className="text-right font-mono">{formatCurrency(document.amountDue)}</p><Input aria-label={`Allocation for ${document.invoiceNumber || document.billNumber}`} type="number" min="0" max={document.amountDue} step="0.01" value={formData.allocations[id] || ''} onChange={(e) => setFormData({ ...formData, allocations: { ...formData.allocations, [id]: Math.min(Number(document.amountDue), Number(e.target.value) || 0) } })} /></div>})}{!openDocuments.length && <p className="p-3 text-center text-xs text-muted-foreground">No open documents for this party. The amount will remain as an advance.</p>}</div></div>}
          <div className="space-y-2">
            <label className="text-sm font-medium">Payment Mode</label>
            <select 
              className="w-full flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm" 
              value={formData.paymentMode} 
              onChange={e => setFormData({ ...formData, paymentMode: e.target.value })}
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="card">Card</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Reference (Optional)</label>
            <Input value={formData.reference} onChange={e => setFormData({ ...formData, reference: e.target.value })} placeholder="Transaction ID" />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>Cancel</Button>
            <Button type="submit" disabled={createPayment.isPending || !formData.partyId || formData.amount <= 0 || allocatedTotal > formData.amount}>Record {tab === 'receipt' ? 'Receipt' : 'Payment'}</Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
